import "../../../i18n/i18n";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { useState } from "react";
import { useAppStyles } from "../../styles/appStyles";
import { 
  Button, 
  Field, 
  Textarea, 
  Dropdown, 
  Option,
  Spinner,
  tokens, 
  makeStyles,
  MessageBar,
  Text,
  Toolbar,
  ToolbarButton,
  Tooltip
} from "@fluentui/react-components";
import { ArrowLeft20Regular, Mail24Regular } from "@fluentui/react-icons";
import { CarboWasteEndpoints_SendMessage } from "../../../api/endpoints";
import { useDevice } from '../../../model/DeviceContext';

interface ContactFormProps {
  onBack: () => void;
}

const useContactFormStyles = makeStyles({
  contentContainer: {
    padding: tokens.spacingVerticalM,
  },
  formField: {
    marginBottom: tokens.spacingVerticalM,
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalL,
  },
  messageBar: {
    marginBottom: tokens.spacingVerticalM,
  },
  successContainer: {
    textAlign: "center",
    padding: tokens.spacingVerticalL,
  },
  userInfoNote: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

// Opcje dla pola Subject
const SUBJECT_OPTIONS = [
  { key: "technical_issue", text: "contact_subject_technical_issue" },
  { key: "feature_request", text: "contact_subject_feature_request" },
  { key: "bug_report", text: "contact_subject_bug_report" },
  { key: "general_question", text: "contact_subject_general_question" },
  { key: "data_issue", text: "contact_subject_data_issue" },
  { key: "performance_issue", text: "contact_subject_performance_issue" },
  { key: "user_interface", text: "contact_subject_user_interface" },
  { key: "documentation", text: "contact_subject_documentation" },
  { key: "other", text: "contact_subject_other" },
];

const ContactForm: React.FC<ContactFormProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const contactFormStyles = useContactFormStyles();
  const { userInfo } = useDevice();

  const [subject, setSubject] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  // Funkcja wysyłania formularza
  const handleSubmit = async () => {
    if (!userInfo?.name || !userInfo?.email) {
      setError(t("contact_error_user_info_missing", "Brak informacji o użytkowniku"));
      return;
    }

    if (!subject || !message.trim()) {
      setError(t("contact_error_required_fields", "Wypełnij wszystkie wymagane pola"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const selectedOption = SUBJECT_OPTIONS.find(opt => opt.key === subject);
      const subjectText = selectedOption ? t(selectedOption.text) : subject;
      
      const contactRequest = {
        Name: userInfo.name,
        Email: userInfo.email,
        Subject: subjectText,
        Message: message.trim(),
      };

      // Send contact message to API
      const response = await fetch(CarboWasteEndpoints_SendMessage, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactRequest),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Sukces - pokaż komunikat i wróć do poprzedniej strony po 2 sekundach
      setSuccess(true);
      setTimeout(() => {
        onBack();
      }, 2000);

    } catch (error) {
      console.error("Error sending contact message:", error);
      setError(error instanceof Error ? error.message : t("contact_error_send_failed", "Nie udało się wysłać wiadomości"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onBack();
  };

  // Jeśli formularz został wysłany pomyślnie
  if (success) {
    return (
      <main className={styles.appPageContainer}>
        <div className={styles.appPageHeader}>
          <Mail24Regular className={styles.appPageHeaderIcon} />
          <Text className={styles.appPageTitle}>
            {t("contact_form_title", "Kontakt z pomocą techniczną")}
          </Text>
        </div>
        <div className={styles.appPageContent}>
          <div className={contactFormStyles.successContainer}>
            <MessageBar intent="success" className={contactFormStyles.messageBar}>
              {t("contact_success_message", "Wiadomość została wysłana pomyślnie")}
            </MessageBar>
            <p>{t("contact_success_redirect", "Za chwilę zostaniesz przekierowany...")}</p>
            <Spinner label={t("contact_redirecting", "Przekierowywanie...")} />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.appPageContainer}>
      <div className={styles.appPageHeader}>
        <Mail24Regular className={styles.appPageHeaderIcon} />
        <Text className={styles.appPageTitle}>
          {t("contact_form_title", "Kontakt z pomocą techniczną")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip
          content={t("contact_form.toolbar.back_tooltip", "Powrót do poprzedniej strony")}
          relationship="description"
        >
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("contact_form.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={onBack}
          >
            {t("contact_form.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>
      
      <div className={styles.appPageContent}>
        {error && (
          <MessageBar intent="error" className={contactFormStyles.messageBar}>
            {error}
          </MessageBar>
        )}

        <p>{t("contact_form_description", "Wypełnij formularz, aby skontaktować się z naszym zespołem pomocy technicznej")}</p>

        {/* Pole Subject - lista rozwijana */}
        <Field
          label={t("contact_field_subject", "Temat")}
          required
          className={contactFormStyles.formField}
        >
          <Dropdown
            placeholder={t("contact_subject_placeholder", "Wybierz temat")}
            value={subject ? t(SUBJECT_OPTIONS.find(opt => opt.key === subject)?.text || "") : ""}
            selectedOptions={subject ? [subject] : []}
            onOptionSelect={(_, data) => {
              setSubject(data.optionValue || "");
            }}
          >
            {SUBJECT_OPTIONS.map((option) => (
              <Option key={option.key} value={option.key}>
                {t(option.text)}
              </Option>
            ))}
          </Dropdown>
        </Field>

        {/* Pole Message - textarea */}
        <Field
          label={t("contact_field_message", "Wiadomość")}
          required
          className={contactFormStyles.formField}
        >
          <Textarea
            placeholder={t("contact_message_placeholder", "Opisz swój problem lub pytanie...")}
            value={message}
            onChange={(_, data) => setMessage(data.value)}
            rows={8}
            resize="vertical"
          />
        </Field>

        {/* Informacja o danych użytkownika */}
        <p className={contactFormStyles.userInfoNote}>
          {t("contact_user_info_note", "Wiadomość zostanie wysłana jako: {{name}} ({{email}})", { 
            name: userInfo?.name || t("contact_unknown_user", "Nieznany"), 
            email: userInfo?.email || t("contact_unknown_email", "brak") 
          })}
        </p>

        {/* Informacja o zgodzie na przetwarzanie danych */}
        <MessageBar intent="info" className={contactFormStyles.messageBar}>
          {t("contact_privacy_consent", "Wysyłając formularz, wyrażasz zgodę na przetwarzanie podanych danych osobowych w celu udzielenia odpowiedzi na zgłoszenie, zgodnie z naszą Polityką Prywatności.")}
        </MessageBar>

        {/* Przyciski */}
        <div className={contactFormStyles.buttonContainer}>
          <Button 
            appearance="primary" 
            onClick={handleSubmit}
            disabled={isSubmitting || !subject || !message.trim()}
            style={{ width: '100%' }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="tiny" />
                {t("contact_button_sending", "Wysyłanie...")}
              </>
            ) : (
              t("contact_button_send", "Wyślij")
            )}
          </Button>
          <Button 
            appearance="secondary" 
            onClick={handleCancel}
            disabled={isSubmitting}
            style={{ width: '100%' }}
          >
            {t("contact_button_cancel", "Anuluj")}
          </Button>
        </div>
      </div>
    </main>
  );
};

export default ContactForm;
